import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
  type OAuthApi,
} from '@backstage/frontend-plugin-api';
import {
  GITLAB_TOKEN_SCOPES,
  GITLAB_USER_PROXY_PATH,
  GitlabAuthRequiredError,
  isGitlabMergeRequestApiItem,
  isGitlabUnauthorizedStatus,
  mergeGitlabMergeRequests,
  type GitlabMergeRequest,
  type GitlabMergeRequestApiItem,
} from './gitlabMergeRequests';

export type GitlabMergeRequestsApi = {
  fetchOpenMergeRequests(): Promise<GitlabMergeRequest[]>;
};

export const gitlabMergeRequestsApiRef = createApiRef<GitlabMergeRequestsApi>({
  id: 'plugin.home.gitlab-merge-requests',
});

export type GitlabMergeRequestsClientOptions = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  gitlabAuthApi: OAuthApi;
  proxyPath?: string;
};

export class GitlabMergeRequestsClient implements GitlabMergeRequestsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly gitlabAuthApi: OAuthApi;
  private readonly proxyPath: string;

  constructor(opts: GitlabMergeRequestsClientOptions) {
    this.discoveryApi = opts.discoveryApi;
    this.fetchApi = opts.fetchApi;
    this.gitlabAuthApi = opts.gitlabAuthApi;
    this.proxyPath = opts.proxyPath ?? GITLAB_USER_PROXY_PATH;
  }

  async fetchOpenMergeRequests(): Promise<GitlabMergeRequest[]> {
    const authored = await this.listMergeRequests('created_by_me');
    let review: GitlabMergeRequestApiItem[] = [];
    try {
      review = await this.listMergeRequests('assigned_to_me');
    } catch {
      review = [];
    }
    return mergeGitlabMergeRequests(authored, review);
  }

  private async listMergeRequests(
    scope: 'created_by_me' | 'assigned_to_me',
  ): Promise<GitlabMergeRequestApiItem[]> {
    const token = await this.getAccessToken();
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const params = new URLSearchParams({
      state: 'opened',
      scope,
      order_by: 'updated_at',
      sort: 'desc',
      per_page: '8',
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}${this.proxyPath}/merge_requests?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (isGitlabUnauthorizedStatus(response.status)) {
      throw new GitlabAuthRequiredError();
    }
    if (!response.ok) {
      throw new Error(
        `GitLab merge requests request failed (${response.status})`,
      );
    }
    const payload: unknown = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('GitLab merge requests response was not a list');
    }
    return payload.filter(isGitlabMergeRequestApiItem);
  }

  private async getAccessToken(): Promise<string> {
    try {
      const token = await this.gitlabAuthApi.getAccessToken(
        [...GITLAB_TOKEN_SCOPES],
        { optional: true },
      );
      if (!token) {
        throw new GitlabAuthRequiredError();
      }
      return token;
    } catch (error) {
      if (error instanceof GitlabAuthRequiredError) {
        throw error;
      }
      throw new GitlabAuthRequiredError();
    }
  }
}
